import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getPublishedNews, formatNewsDate } from '../../services/newsService';
import './NewsPage.css';

const NewsPage = () => {
  const navigate = useNavigate();
  const [newsArticles, setNewsArticles] = useState([]);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalNews, setTotalNews] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const itemsPerPage = 10;

  // Fetch news from API
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const offset = (currentPage - 1) * itemsPerPage;
        const data = await getPublishedNews({ limit: itemsPerPage, offset });
        
        const articles = data.news || [];
        setNewsArticles(articles);
        setTotalNews(data.total || 0);
        setHasMore(data.hasMore || false);
        
        // Set related articles (different from main articles, up to 5)
        if (articles.length > 1) {
          setRelatedArticles(articles.slice(0, 5));
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news. Please try again later.');
        setNewsArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [currentPage]);

  const handleNewsClick = (article) => {
    navigate(`/news/${article.id}`);
  };

  const handleRelatedClick = (article) => {
    navigate(`/news/${article.id}`);
  };

  const handleLoadMore = () => {
    if (hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Helper to truncate text
  const truncateText = (text, maxLength = 200) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const ChevronRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Loading state
  if (loading && newsArticles.length === 0) {
    return (
      <div className="news-page">
        <Header />
        <main className="news-main">
          <div className="news-container">
            <div className="news-loading">
              <div className="loading-spinner"></div>
              <p>Loading news...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error && newsArticles.length === 0) {
    return (
      <div className="news-page">
        <Header />
        <main className="news-main">
          <div className="news-container">
            <div className="news-error">
              <p>{error}</p>
              <button onClick={() => setCurrentPage(1)} className="retry-btn">Try Again</button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="news-page">
      <Header />
      
      <main className="news-main">
        <div className="news-container">
          {/* Breadcrumb */}
          <div className="news-breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <ChevronRightIcon />
            <span className="breadcrumb-current">News</span>
          </div>

          {/* Page Title */}
          <div className="news-page-header">
            <h1 className="news-page-title">Latest Construction News</h1>
            <p className="news-page-subtitle">Stay updated with the latest news from the construction industry</p>
          </div>

          {/* Content Section */}
          <div className="news-content-wrapper">
            <div className="news-content-section">
              {/* Main Content */}
              <div className="news-main-content">
                {newsArticles.length === 0 ? (
                  <div className="no-news">
                    <p>No news articles available at the moment.</p>
                  </div>
                ) : (
                  newsArticles.map((article) => (
                    <article 
                      key={article.id} 
                      className="news-article"
                      onClick={() => handleNewsClick(article)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Article Header */}
                      <div className="article-header">
                        <span className="article-category">{article.category || 'News'}</span>
                        <h2 className="article-title">{article.title}</h2>
                        <div className="article-meta">
                          <span className="article-date">{formatNewsDate(article.publishedAt || article.createdAt)}</span>
                          {article.authorName && <span className="article-author">By {article.authorName}</span>}
                          <span className="article-views">{article.views || 0} views</span>
                        </div>
                      </div>

                      {/* Main Image */}
                      {article.image && (
                        <div className="article-main-image">
                          <img src={article.image} alt={article.title} onError={(e) => {
                            e.target.style.display = 'none';
                          }} />
                        </div>
                      )}

                      {/* Article Excerpt/Content */}
                      <div className="article-content">
                        <p>{truncateText(article.excerpt || article.content, 250)}</p>
                      </div>

                      {/* Read More */}
                      <div className="article-footer">
                        <span className="read-more-link">Read More →</span>
                      </div>
                    </article>
                  ))
                )}

                {/* Load More Button */}
                {hasMore && (
                  <div className="load-more-container">
                    <button className="load-more-btn" onClick={handleLoadMore} disabled={loading}>
                      {loading ? 'Loading...' : 'Load More News'}
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="news-sidebar">
                <h3 className="sidebar-title">Related Articles</h3>
                <div className="related-articles">
                  {relatedArticles.length > 0 ? (
                    relatedArticles.map((article) => (
                      <div 
                        key={article.id} 
                        className="related-article"
                        onClick={() => handleRelatedClick(article)}
                        style={{ cursor: 'pointer' }}
                      >
                        {article.image && (
                          <img src={article.image} alt={article.title} onError={(e) => {
                            e.target.style.display = 'none';
                          }} />
                        )}
                        <div className="related-article-info">
                          <span className="related-category">{article.category}</span>
                          <h4 className="related-title">{truncateText(article.title, 60)}</h4>
                          <span className="related-date">{formatNewsDate(article.publishedAt || article.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-related">No related articles</p>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPage;
